import { useTranslation } from 'react-i18next';
import { Grid, Typography } from '@mui/material';
import LabelValue from '../../LabelValue';
import { Provider } from '../../../data/models/provider.model';
import insuranceUtils, {
  InsuranceType,
  InsuranceCoverageType,
  InsuranceLimitType,
} from '../../../services/insuranceUtils';
import { useFormatDate } from '../../../hooks/useFormatDate';
import { useInsuranceStrength } from '../../../hooks/useInsuranceStrength';
import { useCurrency } from '../../../hooks/useCurrency';
import { useAutoInsurance } from '../../../hooks/useAutoInsurance';
import { FULLSTORY_MASK_CLASSNAME } from '../../../services/analyticsUtils';

interface InsuranceInfoProps {
  provider: Provider | null;
}

export default function InsuranceInfo({ provider }: InsuranceInfoProps) {
  const { t } = useTranslation('common');

  const commercialInsurance = insuranceUtils.getInsurance(
    provider?.insurancePolicies,
    InsuranceType.General,
  );

  const commercialAggregate = insuranceUtils.getInsuranceLimit(
    commercialInsurance,
    InsuranceCoverageType.AggregateLimit,
    InsuranceLimitType.Aggregate,
  );

  const autoInsurance = insuranceUtils.getInsurance(
    provider?.insurancePolicies,
    InsuranceType.Auto,
  );

  const autoBodilyPerPerson = insuranceUtils.getInsuranceLimit(
    autoInsurance,
    InsuranceCoverageType.SplitLimits,
    InsuranceLimitType.BodilyInjuryPerPerson,
  );

  const autoBodilyPerAccident = insuranceUtils.getInsuranceLimit(
    autoInsurance,
    InsuranceCoverageType.SplitLimits,
    InsuranceLimitType.BodilyInjuryPerAccident,
  );

  const autoPropertyDamage = insuranceUtils.getInsuranceLimit(
    autoInsurance,
    InsuranceCoverageType.SplitLimits,
    InsuranceLimitType.PropertyDamagePerAccident,
  );

  const autoCombined = insuranceUtils.getInsuranceLimit(
    autoInsurance,
    InsuranceCoverageType.CombinedSingleLimit,
    InsuranceLimitType.Combined,
  );

  return (
    <>
      <Grid container spacing={2}>
        <Grid item sm={12}>
          <Typography variant="h6">{t('provider.commercial')}</Typography>
        </Grid>
        <Grid item lg={3} md={6} sm={12}>
          <LabelValue
            label={t('provider.insuranceCompany')}
            value={commercialInsurance?.insuranceCompanyName}
          />
        </Grid>
        <Grid item lg={3} md={6} sm={12}>
          <LabelValue
            className={FULLSTORY_MASK_CLASSNAME}
            label={t('provider.insurancePolicyNum')}
            value={commercialInsurance?.insurancePolicyNumber}
          />
        </Grid>
        <Grid item lg={3} md={6} sm={12}>
          <LabelValue
            label={t('provider.insuranceExpires')}
            value={useFormatDate(commercialInsurance?.insuranceExpiresOn)}
          />
        </Grid>
        <Grid item lg={3} md={6} sm={12}>
          <LabelValue
            label={t('provider.insuranceStrength')}
            value={useInsuranceStrength(
              commercialInsurance?.insuranceStrengthID,
            )}
          />
        </Grid>
        <Grid item lg={3} md={6} sm={12}>
          <LabelValue
            label={t('provider.insuranceEachOccurrence')}
            value={useCurrency(commercialAggregate?.insuredAmount)}
          />
        </Grid>

        <Grid item sm={12}>
          <Typography variant="h6">{t('provider.auto')}</Typography>
        </Grid>
        <Grid item lg={3} md={6} sm={12}>
          <LabelValue
            label={t('provider.insuranceCompany')}
            value={autoInsurance?.insuranceCompanyName}
          />
        </Grid>
        <Grid item lg={3} md={6} sm={12}>
          <LabelValue
            className={FULLSTORY_MASK_CLASSNAME}
            label={t('provider.insurancePolicyNum')}
            value={autoInsurance?.insurancePolicyNumber}
          />
        </Grid>
        <Grid item lg={3} md={6} sm={12}>
          <LabelValue
            label={t('provider.insuranceExpires')}
            value={useFormatDate(autoInsurance?.insuranceExpiresOn)}
          />
        </Grid>
        <Grid item lg={3} md={6} sm={12}>
          <LabelValue
            label={t('provider.insuranceStrength')}
            value={useInsuranceStrength(autoInsurance?.insuranceStrengthID)}
          />
        </Grid>
        {autoBodilyPerPerson?.isActive && (
          <Grid item lg={4} md={6} sm={12}>
            <LabelValue
              label={t('provider.bodilyInjuryPerson')}
              value={useAutoInsurance(autoBodilyPerPerson)}
            />
          </Grid>
        )}
        {autoBodilyPerAccident?.isActive && (
          <Grid item lg={4} md={6} sm={12}>
            <LabelValue
              label={t('provider.bodilyInjuryAccident')}
              value={useAutoInsurance(autoBodilyPerAccident)}
            />
          </Grid>
        )}
        {autoPropertyDamage?.isActive && (
          <Grid item lg={4} md={6} sm={12}>
            <LabelValue
              label={t('provider.propertyDamage')}
              value={useAutoInsurance(autoPropertyDamage)}
            />
          </Grid>
        )}
        {autoCombined?.isActive && (
          <Grid item lg={4} md={6} sm={12}>
            <LabelValue
              label={t('provider.insuranceCombinedLimit')}
              value={useAutoInsurance(autoCombined)}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
}
